// The validation agents on the admin page (internal/validate): each evening's check of the signals
// the app showed (recomputed by separately written rules), the live record of every logged signal
// against the NIFTY 500 and a same-day placebo, beside the backtested record, and a check on demand.
import { useEffect, useState } from 'react';
import { CircleCheckIcon, TriangleAlertIcon } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

interface Issue { rule: string; symbol: string; problem: string }
interface Run { date: string; ok: boolean; summary: string; issues: Issue[] | null }
interface Totals { rule: string; signals: number; closed: number; open: number; avgReturnPct: number; avgVsBenchPct: number; avgVsPlaceboPct: number; winRatePct: number; tested: string }
interface Outcome { date: string; rule: string; symbol: string; entryDate: string; entry: number; exitDate: string; exit: number; open: boolean; returnPct: number; benchPct: number; placeboPct: number; vsPlaceboPct: number }
interface GroupTotals { group: string; from: string; entries: number; trends: number; closed: number; open: number; avgReturnPct: number; vsPlaceboPct: number; vsPlaceboR: number; closedVsPlaceboR: number; winRatePct: number }
interface View { runs: Run[]; record: Totals[]; groups?: GroupTotals[]; outcomes: Outcome[]; loggedDays: number }

const pct = (v: number) => `${v >= 0 ? '+' : '−'}${Math.abs(v).toFixed(2)}%`;
const tone = (v: number) => (v > 0 ? 'text-bull' : v < 0 ? 'text-bear' : '');
const RULE: Record<string, string> = { breakout: 'New-high breakout', drift: 'Results drift' };

export function ValidationPanel() {
  const [view, setView] = useState<View | null>(null);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState<Run | null>(null);
  useEffect(() => { void api('/v1/admin/validation').then((r) => (r.ok ? r.json() : null)).then(setView).catch(() => {}); }, []);
  const checkNow = async () => {
    setBusy(true);
    const r = await api('/v1/admin/validation/check', { method: 'POST' }).catch(() => null);
    const j = r?.ok ? await r.json() : null;
    setBusy(false);
    if (j?.run) { setNow(j.run); toast[j.run.ok ? 'success' : 'warning'](j.run.ok ? 'Every signal matches the rules' : `${j.run.issues.length} issue(s) found`); } else toast.error('The check failed');
  };
  return (
    <section className="mt-10">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-[20px] font-semibold">Validation</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">Every trading day the app's signals are logged after the close, recomputed in the evening by separately written rules, and followed against the NIFTY 500 and a same-day placebo. Results go to Telegram.</p>
        </div>
        <Button variant="outline" className="rounded-full" disabled={busy} onClick={() => void checkNow()}>{busy ? 'Checking…' : 'Check now'}</Button>
      </div>

      {now && <RunRow run={now} label="Now" />}

      <h3 className="mt-6 text-[13px] font-semibold tracking-wide text-muted-foreground uppercase">Live record</h3>
      {!view ? <p className="mt-2 text-sm text-muted-foreground">Loading…</p> : view.record.length === 0 ? (
        <p className="mt-2 text-sm text-muted-foreground">{view.loggedDays ? `${view.loggedDays} day(s) logged; no signal has reached its entry yet (the next open after the signal).` : 'Nothing logged yet: the first log is after today\'s close (16:15), the first check at 19:00.'}</p>
      ) : (
        <div className="mt-2 grid gap-2 md:grid-cols-2">
          {view.record.map((t) => (
            <Card key={t.rule} className="gap-1 px-4 py-3 text-[13px]">
              <div className="font-medium text-ink">{RULE[t.rule] ?? t.rule} <span className="font-normal text-muted-foreground">· {t.signals} signals ({t.closed} closed, {t.open} open)</span></div>
              <div className="num flex flex-wrap gap-x-4">
                <span>avg <b className={tone(t.avgReturnPct)}>{pct(t.avgReturnPct)}</b></span>
                <span>vs NIFTY 500 <b className={tone(t.avgVsBenchPct)}>{pct(t.avgVsBenchPct)}</b></span>
                <span>vs placebo <b className={tone(t.avgVsPlaceboPct)}>{pct(t.avgVsPlaceboPct)}</b></span>
                <span>won {t.winRatePct.toFixed(0)}%</span>
              </div>
              <div className="text-[12px] text-muted-foreground">{t.tested} Few signals mean a noisy record: it needs months.</div>
            </Card>
          ))}
        </div>
      )}

      {view?.groups && view.groups.length > 0 && (
        <>
          <h3 className="mt-6 text-[13px] font-semibold tracking-wide text-muted-foreground uppercase">Breakout scan by group (every stock shown, each evening, from the next open)</h3>
          <Card className="mt-2 gap-0 overflow-x-auto py-0">
            <table className="w-full min-w-[640px] text-[13px]">
              <thead className="text-left text-[11px] tracking-wide text-muted-foreground uppercase"><tr className="border-b">{['Group', 'Since', 'Entries', 'Trends', 'Closed', 'Avg', 'vs placebo', 'R (all)', 'R (closed)', 'Ahead'].map((h) => <th key={h} className="px-2 py-2 font-medium">{h}</th>)}</tr></thead>
              <tbody>
                {view.groups.map((g) => (
                  <tr key={g.group} className="num border-b border-border/60 last:border-b-0">
                    <td className="px-2 py-2 font-semibold">{g.group}</td><td className="px-2 py-2">{g.from}</td><td className="px-2 py-2">{g.entries}</td><td className="px-2 py-2">{g.trends}</td>
                    <td className="px-2 py-2">{g.closed}{g.closed < 30 && <span className="text-muted-foreground"> · too few</span>}</td>
                    <td className={cn('px-2 py-2', tone(g.avgReturnPct))}>{pct(g.avgReturnPct)}</td><td className={cn('px-2 py-2', tone(g.vsPlaceboPct))}>{pct(g.vsPlaceboPct)}</td>
                    <td className="px-2 py-2">{g.vsPlaceboR >= 0 ? '+' : ''}{g.vsPlaceboR.toFixed(2)}R</td><td className="px-2 py-2">{g.closed ? `${g.closedVsPlaceboR >= 0 ? '+' : ''}${g.closedVsPlaceboR.toFixed(2)}R` : '—'}</td>
                    <td className="px-2 py-2">{g.winRatePct.toFixed(0)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
          <p className="mt-1 text-[12px] text-muted-foreground">Tested (Plans, every NSE stock with the delisted): A, the momentum top 20, +0.44R a trade recently; B, other breakout trends, +0.17R. Open entries are marked at the last close; the closed column is the record that counts, once 30+ have closed.</p>
        </>
      )}

      <h3 className="mt-6 text-[13px] font-semibold tracking-wide text-muted-foreground uppercase">Daily checks</h3>
      <div className="mt-2 grid gap-2">
        {view?.runs.length === 0 && <p className="text-sm text-muted-foreground">No check yet.</p>}
        {view?.runs.map((r) => <RunRow key={r.date} run={r} />)}
      </div>

      {view && view.outcomes.length > 0 && (
        <>
          <h3 className="mt-6 text-[13px] font-semibold tracking-wide text-muted-foreground uppercase">Signals followed</h3>
          <Card className="mt-2 gap-0 overflow-x-auto py-0">
            <table className="w-full min-w-[760px] text-[12.5px]">
              <thead className="text-left text-[11px] tracking-wide text-muted-foreground uppercase"><tr className="border-b">
                {['Signal', 'Stock', 'Rule', 'Entry', 'Exit / now', 'Return', 'NIFTY 500', 'Placebo', 'vs placebo'].map((h) => <th key={h} className="px-2 py-1.5 font-medium">{h}</th>)}
              </tr></thead>
              <tbody>
                {[...view.outcomes].reverse().map((o) => (
                  <tr key={o.date + o.rule + o.symbol} className="border-b border-border/50 last:border-b-0">
                    <td className="num px-2 py-1.5">{o.date}</td>
                    <td className="px-2 py-1.5 font-semibold">{o.symbol}</td>
                    <td className="px-2 py-1.5">{RULE[o.rule] ?? o.rule}</td>
                    <td className="num px-2 py-1.5">₹{o.entry.toFixed(2)}<div className="text-[11px] text-muted-foreground">{o.entryDate}</div></td>
                    <td className="num px-2 py-1.5">₹{o.exit.toFixed(2)}<div className="text-[11px] text-muted-foreground">{o.open ? `open · ${o.exitDate}` : o.exitDate}</div></td>
                    <td className={cn('num px-2 py-1.5', tone(o.returnPct))}>{pct(o.returnPct)}</td>
                    <td className={cn('num px-2 py-1.5', tone(o.benchPct))}>{pct(o.benchPct)}</td>
                    <td className={cn('num px-2 py-1.5', tone(o.placeboPct))}>{pct(o.placeboPct)}</td>
                    <td className={cn('num px-2 py-1.5 font-medium', tone(o.vsPlaceboPct))}>{pct(o.vsPlaceboPct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
          <p className="mt-1 text-[12px] text-muted-foreground">After 0.35% delivery costs. Placebo: five random NSE 500 stocks with no signal that day, entered and exited on the same sessions.</p>
        </>
      )}
    </section>
  );
}

function RunRow({ run, label }: { run: Run; label?: string }) {
  const issues = run.issues ?? [];
  return (
    <div className={cn('rounded-xl border px-3 py-2 text-[13px]', run.ok ? 'bg-card' : 'border-warn/40 bg-warn-soft/40')}>
      <div className="flex items-start gap-2">
        {run.ok ? <CircleCheckIcon className="mt-0.5 size-4 shrink-0 text-bull" /> : <TriangleAlertIcon className="mt-0.5 size-4 shrink-0 text-warn-ink" />}
        <span>{label && <b className="mr-1 text-ink">{label}:</b>}{run.summary}</span>
      </div>
      {issues.length > 0 && (
        <ul className="mt-1 ml-6 list-disc text-[12.5px] text-muted-foreground">
          {issues.slice(0, 20).map((i) => <li key={i.rule + i.symbol + i.problem}><b className="text-ink">{i.symbol}</b> ({i.rule}): {i.problem}</li>)}
          {issues.length > 20 && <li>…and {issues.length - 20} more</li>}
        </ul>
      )}
    </div>
  );
}
